import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { API, useAuth } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Check, Crown, Sparkles } from 'lucide-react';
import AuthDialog from '@/components/AuthDialog';

const Membership = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isMember, setIsMember] = useState(false);
  const [showAuth, setShowAuth] = useState(false);

  useEffect(() => {
    if (user) {
      setIsMember(user.is_member);
    }
  }, [user]);

  const handleJoinFree = async () => {
    // If not logged in, show auth dialog
    if (!user) {
      setShowAuth(true);
      return;
    }
    
    setLoading(true);
    try {
      // Call free membership endpoint
      const response = await axios.post(`${API}/payments/join-free`);
      
      if (response.data.success) {
        toast.success('Welcome to ExpatsSphere! Your complimentary membership is now active.');
        // Update user state
        login(localStorage.getItem('token'));
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate membership');
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    'Create unlimited events',
    'RSVP to any event',
    'Join event discussions',
    'Connect with expats worldwide',
    'Priority event visibility',
    'Access to exclusive community features',
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="pt-20 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Hero Section with Benefits Overlay */}
            <div className="relative rounded-3xl overflow-hidden mb-12 border-4 border-border brutal-shadow">
              {/* Hero Image */}
              <img
                src="https://images.unsplash.com/photo-1529156069898-49953e39b3ac?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Diverse group of expats connecting"
                className="w-full h-[580px] md:h-[550px] object-cover"
              />
              {/* Dark Overlay for Better Text Visibility */}
              <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/70 to-black/50"></div>
              
              {/* Benefits Text Overlay */}
              <div className="absolute inset-0 flex flex-col justify-center px-6 md:px-16 pt-4 md:pt-0">
                <div className="max-w-xl">
                  {/* Mobile: Price badge inline with header */}
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                    <div>
                      <div className="inline-flex items-center gap-2 bg-accent/90 text-black px-4 py-2 rounded-full mb-4 font-bold text-sm">
                        <Crown className="w-4 h-4" />
                        Membership Benefits
                      </div>
                      <h1 className="text-3xl md:text-5xl font-heading font-bold text-white leading-tight">
                        {isMember ? "You're a Member!" : 'Join Our Global Community'}
                      </h1>
                    </div>
                  {/* Price Badge - Mobile: inline, Desktop: absolute positioned */}
                  {isMember ? (
                    <div className="md:hidden bg-white/95 backdrop-blur-sm rounded-2xl px-4 py-2 shadow-xl border-2 border-green-500 inline-flex flex-col items-center self-start">
                      <span className="text-2xl font-heading font-bold text-green-600">ACTIVE</span>
                      <span className="text-xs text-muted-foreground">Complimentary</span>
                    </div>
                  ) : (
                    <div className="md:hidden bg-white/95 backdrop-blur-sm rounded-2xl px-4 py-2 shadow-xl border-2 border-accent inline-flex flex-col items-center self-start">
                      <span className="text-lg font-heading font-bold text-primary">Complimentary</span>
                      <span className="text-xs text-muted-foreground">1 year membership</span>
                    </div>
                  )}
                  </div>
                  
                  <div className="space-y-2 md:space-y-3 mb-6 md:mb-8">
                    {benefits.map((benefit, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                        className="flex items-center gap-3"
                      >
                        <div className="w-6 h-6 bg-accent rounded-full flex items-center justify-center flex-shrink-0">
                          <Check className="w-4 h-4 text-black" />
                        </div>
                        <span className="text-white text-base md:text-lg font-medium">{benefit}</span>
                      </motion.div>
                    ))}
                  </div>
                  {!isMember && !user && (
                    <Button
                      onClick={() => setShowAuth(true)}
                      className="bg-accent hover:bg-accent/90 text-black h-12 md:h-14 px-4 md:px-10 rounded-full font-bold text-sm md:text-lg shadow-xl w-full md:w-auto whitespace-nowrap overflow-hidden"
                      data-testid="hero-join-button"
                    >
                      <Sparkles className="w-5 h-5 mr-2 flex-shrink-0" />
                      <span className="truncate">Join Now – Complimentary Membership</span>
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Price Badge Overlay - Desktop only */}
              {isMember ? (
                <div className="hidden md:block absolute top-6 right-6 bg-white/95 backdrop-blur-sm rounded-2xl p-4 shadow-xl border-2 border-green-500">
                  <div className="text-center">
                    <span className="text-3xl font-heading font-bold text-green-600">ACTIVE</span>
                    <p className="text-sm text-muted-foreground">Complimentary Membership</p>
                  </div>
                </div>
              ) : (
                <div className="hidden md:block absolute top-6 right-6 bg-white/95 backdrop-blur-sm rounded-2xl p-4 shadow-xl border-2 border-accent">
                  <div className="text-center">
                    <span className="text-2xl font-heading font-bold text-primary">Complimentary</span>
                    <p className="text-sm text-muted-foreground">1 year membership</p>
                  </div>
                </div>
              )}
            </div>

            {/* Subscribe Section - For logged in non-members */}
            {!isMember && (
              <div className="bg-white rounded-3xl p-8 md:p-12 border-2 border-border brutal-shadow mb-8">
                <div className="text-center mb-8">
                  <h2 className="text-2xl md:text-3xl font-heading font-bold mb-2">
                    {user ? 'Activate Your Complimentary Membership' : 'Ready to Join?'}
                  </h2>
                  <div className="inline-flex flex-col items-center mb-2">
                    <span className="text-3xl font-heading font-bold text-primary">Complimentary</span>
                    <span className="text-sm text-muted-foreground">1 year membership when you join</span>
                  </div>
                  <p className="text-muted-foreground">No credit card required</p>
                </div>

                <Button
                  onClick={handleJoinFree}
                  disabled={loading}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-14 rounded-full font-bold text-lg brutal-shadow"
                  data-testid="subscribe-button"
                >
                  {loading ? (
                    'Activating...'
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      {user ? 'Activate Membership' : 'Sign Up to Join'}
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Member Status Card */}
            {isMember && user?.membership_expires && (
              <div className="bg-white rounded-3xl p-8 md:p-12 border-2 border-border brutal-shadow mb-8">
                <div className="text-center p-4 bg-success/10 rounded-xl">
                  <p className="text-success font-semibold text-lg">
                    Membership active until{' '}
                    {new Date(user.membership_expires).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                </div>
              </div>
            )}

            <div className="text-center text-sm text-muted-foreground">
              <p>Secure payment powered by Stripe</p>
              <p className="mt-2">Cancel anytime • No hidden fees • 100% satisfaction guaranteed</p>
            </div>
          </motion.div>
        </div>
      </div>
      
      {showAuth && (
        <AuthDialog 
          open={showAuth} 
          onClose={() => setShowAuth(false)} 
          customMessage="Sign up to become a member"
          defaultToSignup={true}
        />
      )}
    </div>
  );
};

export default Membership;